import { ColchesterRecordType, ColchesterRecordSubType} from "./ColchesterRecordTypes";
import { WfsParamSourceMapServer, IWfsParamSource } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";
import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { FeatureAdd } from "@esdm/map/dist/layers/vector/sources/loaders";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/MapServer/Hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(null, {srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: ColchesterRecordType,
        recordSubType: ColchesterRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig = null;

        switch (recordType) {
            case ColchesterRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;

            case ColchesterRecordType.Event:
                layerConfig = this.Event(opts);
                break;

        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {
                case ColchesterRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break;

                case ColchesterRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case ColchesterRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case ColchesterRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case ColchesterRecordSubType.LocalList:
                    layerConfig = this.LocalList(opts);
                    break; 
            }
        }

        return layerConfig;
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: ColchesterRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "MonumentPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: ColchesterRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 10,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    },
                    {
                        layerName: "EventPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }
                ]
            }, ...opts
        };
    }

    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationAreas",
                type: ColchesterRecordType.Designation,
                subType: ColchesterRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: ColchesterRecordSubType.ConservationArea},
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: ColchesterRecordType.Designation,
                subType: ColchesterRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: ColchesterRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 16,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 16,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 15
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 15
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: ColchesterRecordType.Designation,
                subType: ColchesterRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: ColchesterRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLineAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: ColchesterRecordType.Designation,
                subType: ColchesterRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: ColchesterRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: false,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

    private LocalList = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Local List",
                ref: "LocalList",
                type: ColchesterRecordType.Designation,
                subType: ColchesterRecordSubType.LocalList,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: ColchesterRecordSubType.LocalList },
                wfsParams: this.paramSource(),
                legend: { point: false, line: false },
                visibleOnFirstLoad: false,                
                alwaysLoadOnZoom:  true,                
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 11,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLineAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    },
                    {
                        layerName: "DesignationPolygonsAsPointsWFS",
                        minZoom: 1,
                        maxZoom: 10
                    }
                ]
            }, ...opts
        };
    }

}

